import { useEffect, useState } from 'preact/hooks';
import { supabase } from '../lib/supabase';
import { useFlag } from '../shell/hooks';
import { useNotice } from '../shell/notice';
import { canUse, differenceField, differenceText, dismissed, keepMineValue, type Difference, type Dismissal } from '../queue/lookups';
import { dismiss } from '../queue/lookupLoad';
import type { BookData } from './types';

// Different from yours in the book page's edit mode (Queue Part 4 step 2; Kristi, Oct 30): what the
// nightly fill-in found that differs from a stored value, under that value's row, with Use this ·
// Keep mine (the same answers as Review's chip, remembered in review_dismissals). Behind
// book_page.differences. The nightly record is admin-read for now, so only the admin sees them.

type Diffs = { diffs: Difference[]; dismissals: Dismissal[] };
const cache = new Map<string, Promise<Diffs>>();

async function load(workId: string, bookId: string): Promise<Diffs> {
  if (!supabase) return { diffs: [], dismissals: [] };
  const [w, d] = await Promise.all([
    supabase.from('work_lookups').select('differences').eq('work_id', workId).maybeSingle(),
    supabase.from('review_dismissals').select('id, book_id, field, kind, found_value').eq('book_id', bookId),
  ]);
  const diffs = Array.isArray((w.data as { differences?: unknown } | null)?.differences) ? ((w.data as { differences: Difference[] }).differences ?? []) : [];
  const dismissals = ((d.data as Record<string, unknown>[] | null) ?? []).map((r) => ({
    id: r.id as string,
    bookId: r.book_id as string,
    field: r.field as string,
    kind: r.kind as Dismissal['kind'],
    foundValue: (r.found_value as string | null) ?? null,
  }));
  return { diffs, dismissals };
}

function useDiffs(book: BookData): [Diffs | null, () => void] {
  const on = useFlag('book_page.differences');
  const workId = book.work.id;
  const [state, setState] = useState<Diffs | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!on) return;
    let live = true;
    if (!cache.has(workId) || tick) cache.set(workId, load(workId, book.id));
    void cache.get(workId)!.then((x) => live && setState(x)).catch(() => live && setState({ diffs: [], dismissals: [] }));
    return () => {
      live = false;
    };
  }, [on, workId, tick]);
  return [on ? state : null, () => setTick((t) => t + 1)];
}

/**
 * The line under a row, when a lookup found something else: "Different from yours: Open Library
 * says 2000 · Use this · Keep mine". `field` is the difference's field; `rowId` an edition's ID.
 */
export function DiffNote({ book, field, rowId, use, current }: { book: BookData; field: string; rowId?: string | null; use?: (found: string) => Promise<boolean>; /** The value stored now: once it's the one found, the note goes. */ current?: string | null }) {
  const notice = useNotice();
  const [diffs, reload] = useDiffs(book);
  const [busy, setBusy] = useState(false);
  if (!diffs) return null;
  const d = diffs.diffs.find((x) => x.field === field && (rowId ? x.rowId === rowId : x.table === 'works'));
  if (!d || (current !== undefined && current !== null && current === d.found)) return null;
  const key = differenceField(d);
  const item = { chip: 'different' as const, book: { id: book.id, title: book.work.title, author: '' } as never, field: key, difference: d };
  if (dismissed(item, new Map(diffs.dismissals.map((x) => [`${x.bookId}:${x.field}`, x])))) return null;
  const t = differenceText(d);
  const keepMine = async () => {
    setBusy(true);
    try {
      const before = diffs.dismissals.find((x) => x.field === key)?.foundValue ?? null;
      const back = await dismiss(book.id, key, 'keep_mine', keepMineValue(d, before));
      reload();
      notice({ message: `Kept yours: ${t.label.toLowerCase()}`, undo: () => void back().then(reload) });
    } catch (e) {
      notice({ message: `Not saved: ${e instanceof Error ? e.message : String(e)}`, done: false });
    }
    setBusy(false);
  };
  return (
    <span class="lk-diff bk-diff" role="note">
      Different from yours: {d.source} {canUse(d) ? `says ${t.found}` : t.found}
      {canUse(d) && use ? (
        <>
          {' · '}
          <button type="button" class="btn-link" disabled={busy} onClick={() => void use(d.found).then((ok) => ok && reload())}>
            Use this
          </button>
        </>
      ) : null}
      {' · '}
      <button type="button" class="btn-link" disabled={busy} onClick={() => void keepMine()}>
        Keep mine
      </button>
    </span>
  );
}
